import React, { FC, useState, useEffect } from 'react';
import { useAsyncDebounce } from 'react-table';
import styled from 'styled-components';
import InputGroup from 'react-bootstrap/InputGroup';
import FormControl from 'react-bootstrap/FormControl';
import { FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

export interface IGlobalFilterProps {
  className?: string;
  globalFilter: string;
  setGlobalFilter: (x: string) => void;
  showFilters?: boolean;
}

/*
 * `GlobalFilter` implements global row filtering through `useGlobalFilter` react-table hook.
 * In other words, filtering based upon data that may be in any column in a given row.
 *
 * Doc: https://react-table.tanstack.com/docs/api/useGlobalFilter
 */
const GlobalFilter: FC<IGlobalFilterProps> = ({
  className,
  globalFilter,
  setGlobalFilter,
  showFilters = true,
}: IGlobalFilterProps) => {
  const [value, setValue] = useState(globalFilter);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { t } = useTranslation();
  const onChange = useAsyncDebounce(val => {
    setGlobalFilter(val || undefined);
  }, 500);

  // Update the window width state on window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!showFilters && windowWidth < 768) {
    return null;
  }

  return (
    <FlexWrapper className={className}>
      <InputGroup className="ml-md-0 align-self-md-start">
        <InputGroup.Prepend>
          <InputGroup.Text id="search">
            <FaSearch size={16} />
          </InputGroup.Text>
        </InputGroup.Prepend>
        <FormControl
          aria-label="Search"
          aria-describedby="search"
          type="text"
          placeholder={t('table.search') || ''}
          value={value || ''}
          onChange={e => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
        />
      </InputGroup>
    </FlexWrapper>
  );
};

const FlexWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  &:not([class*='col-']) {
    width: 200px;
  }

  &[class*='col-'] {
    .input-group {
      width: 100%;
    }
  }

  @media ${props => props.theme.breakpoints.lg} {
    width: 50%;

    .input-group {
      width: 100%;
    }
  }

  @media ${props => props.theme.breakpoints.md} {
    width: 100%;
    margin-bottom: 8px;

    .input-group {
      width: 100%;
    }
  }
`;

export default GlobalFilter;
